import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

export default function FloatingContactButton() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isWhatsAppHovered, setIsWhatsAppHovered] = useState(false);

  // WhatsApp phone number (Indian number)
  const whatsappNumber = "919966969809"; // +91 996 696 9809 without + and spaces
  const whatsappUrl = `https://wa.me/${whatsappNumber}`;

  // Show button after page loads with animation
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 300);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  // Handle click - navigate to contact page
  const handleClick = () => {
    navigate("/contact");
  };

  // Handle WhatsApp click - open WhatsApp
  const handleWhatsAppClick = (e) => {
    e.stopPropagation();
    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Contact Button */}
      <div
        className="floating-contact-button"
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          position: "fixed",
          left: "0px",
          top: "calc(50% - 70px)",
          zIndex: 9998,
          cursor: "pointer",
          transition: "all 0.3s ease",
          animation: "slideInLeft 0.5s ease-out",
        }}
      >
        {/* Vertical line bar */}
        <div
          style={{
            position: "relative",
            width: isHovered ? "70px" : "8px",
            height: isHovered ? "120px" : "100px",
            background: isHovered
              ? "linear-gradient(180deg, #0EA5E9, #3B82F6, #2563EB)"
              : "linear-gradient(180deg, #3B82F6, #2563EB, #1E40AF)",
            borderRadius: isHovered ? "0 12px 12px 0" : "0 8px 8px 0",
            boxShadow: isHovered
              ? "0 4px 20px rgba(37, 99, 235, 0.5), inset 0 0 10px rgba(14, 165, 233, 0.3)"
              : "0 2px 10px rgba(37, 99, 235, 0.3)",
            transition: "all 0.3s ease",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          {/* Glowing effect */}
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "100%",
              height: "100%",
              background: isHovered
                ? "linear-gradient(180deg, transparent, rgba(14, 165, 233, 0.4), transparent)"
                : "linear-gradient(180deg, transparent, rgba(59, 130, 246, 0.2), transparent)",
              animation: "glow 2s ease-in-out infinite",
            }}
          />

          {/* Icon container with background circle */}
          <div
            style={{
              position: "relative",
              zIndex: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              height: "100%",
              transition: "all 0.3s ease",
            }}
          >
            {/* Icon with circular background - always visible but smaller when not hovered */}
            <div
              style={{
                width: isHovered ? "50px" : "28px",
                height: isHovered ? "50px" : "28px",
                borderRadius: "50%",
                background: "rgba(255, 255, 255, 0.3)",
                backdropFilter: "blur(6px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.3s ease",
                boxShadow: isHovered ? "0 3px 10px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.3)" : "none",
                border: isHovered ? "2px solid rgba(255, 255, 255, 0.5)" : "none",
                opacity: 1,
                transform: isHovered ? "scale(1)" : "scale(0.9)",
              }}
            >
              <i
                className="bi bi-telephone-fill"
                style={{
                  fontSize: isHovered ? "24px" : "14px",
                  color: "#fff",
                  transition: "all 0.3s ease",
                  transform: isHovered ? "rotate(15deg)" : "rotate(0deg)",
                  textShadow: "0 2px 6px rgba(0, 0, 0, 0.5)",
                  filter: "drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3))",
                }}
              />
            </div>
          </div>
        </div>

        {/* Tooltip text */}
        <div
          className="contact-tooltip"
          style={{
            position: "absolute",
            left: isHovered ? "80px" : "10px",
            top: "50%",
            transform: "translateY(-50%)",
            background: "linear-gradient(135deg, #3B82F6, #2563EB)",
            color: "#fff",
            padding: "8px 14px",
            borderRadius: "6px",
            fontSize: "13px",
            fontWeight: "600",
            whiteSpace: "nowrap",
            opacity: isHovered ? 1 : 0,
            pointerEvents: "none",
            transition: "all 0.3s ease",
            boxShadow: "0 4px 15px rgba(37, 99, 235, 0.4)",
            fontFamily: "'Poppins', sans-serif",
          }}
        >
          Contact Us
          <div
            style={{
              position: "absolute",
              left: "-6px",
              top: "50%",
              transform: "translateY(-50%)",
              width: 0,
              height: 0,
              borderTop: "6px solid transparent",
              borderBottom: "6px solid transparent",
              borderRight: "6px solid #3B82F6",
            }}
          />
        </div>
      </div>

      {/* WhatsApp Button */}
      <div
        className="floating-whatsapp-button"
        onClick={handleWhatsAppClick}
        onMouseEnter={() => setIsWhatsAppHovered(true)}
        onMouseLeave={() => setIsWhatsAppHovered(false)}
        style={{
          position: "fixed",
          left: "0px",
          top: "calc(50% + 70px)",
          zIndex: 9998,
          cursor: "pointer",
          transition: "all 0.3s ease",
          animation: "slideInLeft 0.5s ease-out 0.2s both",
        }}
      >
        {/* Vertical line bar */}
        <div
          style={{
            position: "relative",
            width: isWhatsAppHovered ? "70px" : "8px",
            height: isWhatsAppHovered ? "120px" : "100px",
            background: isWhatsAppHovered
              ? "linear-gradient(180deg, #25D366, #128C7E, #075E54)"
              : "linear-gradient(180deg, #128C7E, #075E54, #064E42)",
            borderRadius: isWhatsAppHovered ? "0 12px 12px 0" : "0 8px 8px 0",
            boxShadow: isWhatsAppHovered
              ? "0 4px 20px rgba(37, 211, 102, 0.5), inset 0 0 10px rgba(37, 211, 102, 0.3)"
              : "0 2px 10px rgba(18, 140, 126, 0.3)",
            transition: "all 0.3s ease",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          {/* Glowing effect */}
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "100%",
              height: "100%",
              background: isWhatsAppHovered
                ? "linear-gradient(180deg, transparent, rgba(37, 211, 102, 0.4), transparent)"
                : "linear-gradient(180deg, transparent, rgba(18, 140, 126, 0.2), transparent)",
              animation: "glow 2s ease-in-out infinite",
            }}
          />

          {/* Icon container with background circle */}
          <div
            style={{
              position: "relative",
              zIndex: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              height: "100%",
              transition: "all 0.3s ease",
            }}
          >
            {/* Icon with circular background - always visible but smaller when not hovered */}
            <div
              style={{
                width: isWhatsAppHovered ? "50px" : "28px",
                height: isWhatsAppHovered ? "50px" : "28px",
                borderRadius: "50%",
                background: "rgba(255, 255, 255, 0.3)",
                backdropFilter: "blur(6px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.3s ease",
                boxShadow: isWhatsAppHovered ? "0 3px 10px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.3)" : "none",
                border: isWhatsAppHovered ? "2px solid rgba(255, 255, 255, 0.5)" : "none",
                opacity: 1,
                transform: isWhatsAppHovered ? "scale(1)" : "scale(0.9)",
              }}
            >
              {/* WhatsApp SVG Icon */}
              <svg
                width={isWhatsAppHovered ? "24" : "14"}
                height={isWhatsAppHovered ? "24" : "14"}
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{
                  transition: "all 0.3s ease",
                  transform: isWhatsAppHovered ? "rotate(15deg)" : "rotate(0deg)",
                  filter: "drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3))",
                }}
              >
                <path
                  d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"
                  fill="#fff"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Tooltip text */}
        <div
          className="whatsapp-tooltip"
          style={{
            position: "absolute",
            left: isWhatsAppHovered ? "80px" : "10px",
            top: "50%",
            transform: "translateY(-50%)",
            background: "linear-gradient(135deg, #25D366, #128C7E)",
            color: "#fff",
            padding: "8px 14px",
            borderRadius: "6px",
            fontSize: "13px",
            fontWeight: "600",
            whiteSpace: "nowrap",
            opacity: isWhatsAppHovered ? 1 : 0,
            pointerEvents: "none",
            transition: "all 0.3s ease",
            boxShadow: "0 4px 15px rgba(37, 211, 102, 0.4)",
            fontFamily: "'Poppins', sans-serif",
          }}
        >
          Chat on WhatsApp
          <div
            style={{
              position: "absolute",
              left: "-6px",
              top: "50%",
              transform: "translateY(-50%)",
              width: 0,
              height: 0,
              borderTop: "6px solid transparent",
              borderBottom: "6px solid transparent",
              borderRight: "6px solid #25D366",
            }}
          />
        </div>
      </div>

      {/* CSS Animation */}
      <style>{`
        @keyframes slideInLeft {
          0% {
            opacity: 0;
            transform: translateY(-50%) translateX(-50px);
          }
          100% {
            opacity: 1;
            transform: translateY(-50%) translateX(0);
          }
        }

        @keyframes glow {
          0%, 100% {
            opacity: 0.3;
            transform: translate(-50%, -50%) translateY(0);
          }
          50% {
            opacity: 0.6;
            transform: translate(-50%, -50%) translateY(-10px);
          }
        }

        @media (max-width: 768px) {
          .floating-contact-button,
          .floating-whatsapp-button {
            left: 0px !important;
          }
          
          .contact-tooltip,
          .whatsapp-tooltip {
            display: none !important;
          }
        }
      `}</style>
    </>
  );
}

